/** @typedef {import('./types').CrmQuoteInput} CrmQuoteInput */
/** @typedef {import('./types').CrmProposal} CrmProposal */

export const DEFAULT_QUOTE = { Plan: 'pro', Sites: 1, Users: 5, Billing: 'annual', Onboarding: true }

const toInt = value => {
    const n = Math.floor(Number(value))
    return Number.isFinite(n) ? n : 0
}

/** Start values for the dialog: the deal's saved proposal if it has one, else sensible defaults. */
export const initialQuote = proposal =>
    proposal
        ? {
              Plan: proposal.Plan,
              Sites: proposal.Sites,
              Users: proposal.Users,
              Billing: proposal.Billing,
              Onboarding: Boolean(proposal.Onboarding),
          }
        : { ...DEFAULT_QUOTE }

/** Whole-number sites/users of at least 1 are required before a quote is requested. */
export const isQuoteReady = quote => toInt(quote.Sites) >= 1 && toInt(quote.Users) >= 1 && Boolean(quote.Plan)

/** Body for Quote / Proposal: integers, no stray fields. @param {CrmQuoteInput} quote */
export const quoteBody = quote => ({
    Plan: quote.Plan,
    Sites: toInt(quote.Sites),
    Users: toInt(quote.Users),
    Billing: quote.Billing,
    Onboarding: Boolean(quote.Onboarding),
})

/**
 * Rows for the price breakdown, all taken from the server's Proposal. The annual discount amount is
 * the only derived figure (12 x monthly minus the annual value) and is shown for annual billing only.
 * @param {CrmProposal} proposal
 * @param {{SitePrice: number}|undefined} plan
 * @param {number} extraUserPrice
 * @returns {{ label: string, amount: number, strong?: boolean }[]}
 */
export const quoteLines = (proposal, plan, extraUserPrice) => {
    const lines = []
    const sitePrice = plan?.SitePrice
    lines.push({
        label: sitePrice
            ? `${proposal.Sites} site${proposal.Sites === 1 ? '' : 's'} × $${sitePrice} / month (${proposal.PlanName})`
            : `${proposal.Sites} sites (${proposal.PlanName})`,
        amount: sitePrice ? proposal.Sites * sitePrice : proposal.Monthly,
    })
    lines.push({
        label: proposal.ExtraUsers
            ? `${proposal.ExtraUsers} extra user${proposal.ExtraUsers === 1 ? '' : 's'} × $${extraUserPrice} / month`
            : `${proposal.IncludedUsers} users included`,
        amount: proposal.ExtraUsers ? proposal.ExtraUsers * extraUserPrice : 0,
    })
    lines.push({ label: 'Monthly subtotal', amount: proposal.Monthly })
    if (proposal.Billing === 'annual') {
        const discount = proposal.Monthly * 12 - proposal.AnnualValue
        lines.push({
            label: `Annual billing discount (${Math.round(proposal.AnnualDiscount * 100)}%)`,
            amount: -Math.max(0, discount),
        })
    }
    lines.push({ label: 'Annual contract value', amount: proposal.AnnualValue, strong: true })
    if (proposal.Onboarding) lines.push({ label: 'One-time onboarding & template setup', amount: proposal.OnboardingFee })
    return lines
}
